import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { projects } from "../content/projects";
import {
  metadataForPath,
  SITE_NAME,
  type PageMetadata,
} from "../seo/siteMetadata";

/** Creates, updates, or removes one named/property metadata element in the document head. */
function setMeta(
  attribute: "name" | "property",
  key: string,
  content: string | null,
): void {
  const selector = `meta[${attribute}="${key}"]`;
  const existing = document.head.querySelector<HTMLMetaElement>(selector);

  if (content === null) {
    existing?.remove();
    return;
  }

  const element = existing ?? document.createElement("meta");
  element.setAttribute(attribute, key);
  element.content = content;

  if (!existing) {
    document.head.append(element);
  }
}

/** Creates, updates, or removes the canonical-link element for client-side navigation. */
function setCanonical(url: string | null): void {
  const existing = document.head.querySelector<HTMLLinkElement>(
    'link[rel="canonical"]',
  );

  if (url === null) {
    existing?.remove();
    return;
  }

  const element = existing ?? document.createElement("link");
  element.rel = "canonical";
  element.href = url;

  if (!existing) {
    document.head.append(element);
  }
}

/** Applies one metadata projection to the live browser document after SPA navigation. */
function applyMetadata(metadata: PageMetadata): void {
  document.title = metadata.title;

  setMeta("name", "description", metadata.description);
  setMeta("name", "robots", metadata.robots);
  setMeta("name", "author", SITE_NAME);

  setMeta("property", "og:site_name", SITE_NAME);
  setMeta("property", "og:locale", "en_US");
  setMeta("property", "og:title", metadata.title);
  setMeta("property", "og:description", metadata.description);
  setMeta("property", "og:type", metadata.ogType);
  setMeta("property", "og:url", metadata.canonicalUrl);

  setMeta("name", "twitter:card", "summary");
  setMeta("name", "twitter:title", metadata.title);
  setMeta("name", "twitter:description", metadata.description);

  setCanonical(metadata.canonicalUrl);
}

/** Keeps document title, canonical, search, and sharing metadata synchronized with the active client-side route. */
export default function RouteMetadata(): null {
  const location = useLocation();
  const metadata = metadataForPath(location.pathname, projects);

  useEffect(() => {
    applyMetadata(metadata);
  }, [
    metadata.canonicalUrl,
    metadata.description,
    metadata.ogType,
    metadata.robots,
    metadata.title,
  ]);

  return null;
}
